import {
  createRootRouteWithContext,
  Link,
  Outlet,
  Scripts,
} from '@tanstack/react-router'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { lazy, Suspense, useEffect } from 'react'
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals'
import { DevToursSwitcher } from '@/components/dev/dev-tours-switcher'
import { DevUserSwitcher } from '@/components/dev/dev-user-switcher'
import { PostHogVisibilityManager } from '@/providers/posthog-visibility-manager'
import { Toaster } from '@/providers/toaster-provider'
import { TourProvider } from '@/providers/tour-provider'
import type { AuthState } from '@/hooks/use-auth'
import type { QueryClient } from '@tanstack/react-query'
import '@/lib/tours/styles.css'

const TanStackDevtools = import.meta.env.DEV
  ? lazy(() => import('@tanstack/react-devtools').then(m => ({ default: m.TanStackDevtools })))
  : () => <></>

const ReactQueryDevtoolsPanel = import.meta.env.DEV
  ? lazy(() => import('@tanstack/react-query-devtools').then(m => ({ default: m.ReactQueryDevtoolsPanel })))
  : () => <></>

const TanStackRouterDevtoolsPanel = import.meta.env.DEV
  ? lazy(() => import('@tanstack/react-router-devtools').then(m => ({ default: m.TanStackRouterDevtoolsPanel })))
  : () => <></>

const FormDevtoolsPanel = import.meta.env.DEV
  ? lazy(() => import('@tanstack/react-form-devtools').then(m => ({ default: m.FormDevtoolsPanel })))
  : () => <></>

const POSTHOG_PROXY_HOST = 'https://t.supplyco.ai'

// Set dev favicon with hammer emoji to distinguish from production
function setDevFavicon() {
  const canvas = document.createElement('canvas')
  canvas.width = 64
  canvas.height = 64
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return
  }

  ctx.font = '56px serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('🔨', 32, 36)

  const link
    = (document.querySelector('link[rel*=\'icon\']') as HTMLLinkElement)
      || document.createElement('link')
  link.type = 'image/x-icon'
  link.rel = 'icon'
  link.href = canvas.toDataURL()
  document.head.append(link)
}

if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || POSTHOG_PROXY_HOST,
    defaults: '2026-01-30',

    // Enable session recording from the start (will capture anonymous sessions too)
    disable_session_recording: false,

    // Capture unhandled JS errors automatically
    capture_exceptions: true,

    // Capture pageviews automatically
    capture_pageview: true,
    capture_pageleave: true,

    // Privacy controls - mask sensitive data
    mask_all_text: import.meta.env.MODE === 'production',
    mask_all_element_attributes: false,

    // Session configuration
    session_recording: {
      // Mask text content in recordings for privacy
      maskTextSelector: '[data-ph-mask]',
      // Block certain elements from recording
      blockSelector: '[data-ph-no-capture]',
      // Don't attempt to record inside cross-origin iframes.
      // rrweb throws SecurityError when accessing their Window objects.
      recordCrossOriginIframes: false,
    },

    // Advanced options
    persistence: 'localStorage',
    persistence_name: '[ph_supplyco]',
    cross_subdomain_cookie: false,
    secure_cookie: import.meta.env['MODE'] === 'production', // eslint-disable-line dot-notation
  })
}

onCLS(metric => posthog.capture('web_vital', metric))
onINP(metric => posthog.capture('web_vital', metric))
onFCP(metric => posthog.capture('web_vital', metric))
onLCP(metric => posthog.capture('web_vital', metric))
onTTFB(metric => posthog.capture('web_vital', metric))

export interface MyRouterContext {
  auth: AuthState
  queryClient: QueryClient
}

function RootComponent() {
  useEffect(() => {
    if (import.meta.env.DEV) {
      setDevFavicon()
    }
  }, [])

  return (
    <>
      {import.meta.env.DEV && (
        <Suspense>
          <TanStackDevtools
            plugins={[
              {
                name: 'TanStack Query',
                render: <ReactQueryDevtoolsPanel />,
                defaultOpen: true,
              },
              {
                name: 'TanStack Router',
                render: <TanStackRouterDevtoolsPanel />,
                defaultOpen: false,
              },
              {
                name: 'TanStack Form',
                render: <FormDevtoolsPanel />,
                defaultOpen: false,
              },
            ]}
          />
        </Suspense>
      )}
      <PostHogProvider client={posthog}>
        <TourProvider>
          <Toaster />
          <PostHogVisibilityManager />
          {(import.meta.env.DEV || import.meta.env.VERCEL_ENV === 'preview') && (
            <>
              <DevUserSwitcher />
              <DevToursSwitcher />
            </>
          )}
          <div className="h-screen w-screen overflow-hidden">
            <Outlet />
          </div>
          <Scripts />
        </TourProvider>
      </PostHogProvider>
    </>
  )
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
  notFoundComponent: () => (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
      <h1 className="text-4xl font-bold mb-4">404 - Page Not Found</h1>
      <p className="text-lg text-muted-foreground mb-6">
        The page you are looking for doesn&apos;t exist or has been moved.
      </p>
      <Link
        to="/"
        className="px-4 py-2 rounded bg-primary text-primary-foreground hover:bg-primary/90"
      >
        Return Home
      </Link>
    </div>
  ),
})
